/**
 * Spotlight tour of the For You page.
 *
 * Runs once per customer after the carousels are on screen. Typical flow:
 * primary carousel header → primary books scroll then shelf menu → for each
 * other-recipient *type*: section header tooltip, then books scroll
 * (auto-advances, no second tooltip) → feedback tab. Duplicate carousels of
 * the same `recipient_type` are skipped (see `resolveTourTarget`). The
 * two-question feedback popup opens later, once the shopper has actually
 * browsed — see `useFeedbackPrompt`.
 *
 * A step may name a `leadInSelector`: the spotlight rests on that larger
 * element, under a caption of its own, before contracting onto the real target.
 * Scroll-preview steps use it to hold on the row of covers and glide it
 * sideways, so the shopper sees which books are being talked about — and that
 * there are more of them — instead of jumping straight to a small target.
 * Steps with `autoAdvance` skip the target-phase tooltip after the lead-in and
 * move straight to the next step (used for recipient scroll-only previews).
 *
 * Targets are found by `data-tour-*` attribute rather than by ref, so the tour
 * stays decoupled from the carousel components it explains.
 */

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TourTarget } from '../utils/tourTarget';
import { SHELF_MENU_PREVIEW_EVENT, SHELF_MENU_TOUR_ATTR } from './BookShelfMenu';
import { FEEDBACK_TAB_TOUR_ATTR } from './FeedbackSideTab';

const SEEN_KEY_PREFIX = 'indigo.forYou.tourSeen.';

/** Padding around the spotlit element, in pixels. */
const SPOTLIGHT_PADDING = 8;
/** Tooltip width; also the minimum room needed beside a target. */
const TOOLTIP_WIDTH = 300;
/** How long the spotlight rests on a step's lead-in before contracting. */
const LEAD_IN_MS = 4600;
/** Beats inside the lead-in: glide the row sideways, then bring it back. */
const LEAD_IN_GLIDE_OUT_MS = 900;
const LEAD_IN_GLIDE_BACK_MS = 2900;
/** How long each leg of that glide takes. */
const LEAD_IN_GLIDE_MS = 1100;
/** How much of the row to glide past, as a share of its visible width. */
const LEAD_IN_GLIDE_FRACTION = 0.5;
/** Plus one more cover on top of that share, so a whole new one comes into view. */
const LEAD_IN_GLIDE_EXTRA_COVERS = 1;
/** How long the tour takes to bring a target to the middle of the viewport. */
const SCROLL_MS = 900;
/** Beat between the page settling and the spotlight appearing over it. */
const SETTLE_MS = 250;

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const easeInOutCubic = (progress: number): number =>
  progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;

/**
 * Brings an element to the middle of the viewport over `SCROLL_MS`.
 *
 * Replaces `scrollIntoView({ behavior: 'smooth' })`, which runs at the
 * browser's own pace and reports nothing when it lands — so the spotlight had
 * to guess, and appeared over a page that was still moving. Fixed elements are
 * left alone: scrolling cannot bring them anywhere they are not already.
 *
 * @returns a cancel function. `onSettled` runs once the page is still.
 */
const scrollToCentre = (element: HTMLElement, onSettled: () => void): (() => void) => {
  const from = window.scrollY;
  const box = element.getBoundingClientRect();
  const furthest = document.documentElement.scrollHeight - window.innerHeight;
  const to = Math.max(
    0,
    Math.min(from + box.top + box.height / 2 - window.innerHeight / 2, furthest)
  );
  const distance = to - from;
  const isFixed = window.getComputedStyle(element).position === 'fixed';

  if (isFixed || prefersReducedMotion() || Math.abs(distance) < 2) {
    if (!isFixed) window.scrollTo(0, to);
    onSettled();
    return () => {};
  }

  let frame = 0;
  const startedAt = performance.now();
  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / SCROLL_MS);
    window.scrollTo(0, from + distance * easeInOutCubic(progress));
    if (progress < 1) {
      frame = requestAnimationFrame(tick);
      return;
    }
    onSettled();
  };

  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
};

/**
 * Glides a row's horizontal scroll to `to` over `LEAD_IN_GLIDE_MS`.
 *
 * `scrollTo({ behavior: 'smooth' })` sets its own duration from the distance,
 * and for half a carousel that is a flick: the covers had gone by before the
 * shopper followed them anywhere. Reduced-motion shoppers never reach this —
 * they skip the lead-in altogether.
 *
 * @returns a cancel function.
 */
const glideRow = (row: HTMLElement, to: number): (() => void) => {
  const from = row.scrollLeft;
  const distance = to - from;
  if (Math.abs(distance) < 2) return () => {};

  // The row carries `scroll-smooth` for its arrow buttons. Left on, it turns
  // every frame set here into a fresh smooth scroll of its own, and the damping
  // means the sweep never arrives where it was sent.
  const behaviour = row.style.scrollBehavior;
  row.style.scrollBehavior = 'auto';
  const restore = () => {
    row.style.scrollBehavior = behaviour;
  };

  let frame = 0;
  const startedAt = performance.now();
  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / LEAD_IN_GLIDE_MS);
    row.scrollLeft = from + distance * easeInOutCubic(progress);
    if (progress < 1) {
      frame = requestAnimationFrame(tick);
      return;
    }
    restore();
  };

  frame = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(frame);
    restore();
  };
};

/**
 * Distance from one cover to the next, gap included.
 *
 * Measured off the covers rather than held as a constant: the row fits 2.5 of
 * them on a phone and 6.5 on a wide screen, so a fixed width would mean a
 * different amount of travel at every breakpoint.
 */
const coverPitch = (row: HTMLElement): number => {
  const [first, second] = Array.from(row.children) as HTMLElement[];
  if (!first) return 0;
  return second ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width;
};

export const hasSeenTour = (customerId?: string): boolean => {
  if (!customerId) return true;
  try {
    return localStorage.getItem(`${SEEN_KEY_PREFIX}${customerId}`) === '1';
  } catch {
    // Private browsing can throw on access; treat it as "already seen" so the
    // tour never loops for someone who cannot persist the flag.
    return true;
  }
};

const markTourSeen = (customerId?: string): void => {
  if (!customerId) return;
  try {
    localStorage.setItem(`${SEEN_KEY_PREFIX}${customerId}`, '1');
  } catch {
    // Nothing to do — worst case the shopper sees the tour again next visit.
  }
};

interface Step {
  selector: string;
  /** Spotlit for a beat before `selector`, to frame what the step is about. */
  leadInSelector?: string;
  /** Shown while the lead-in holds, in place of the step tooltip. */
  leadInCaption?: string;
  /** Hold the target's shelf menu open, so its choices are readable. */
  previewsShelfMenu?: boolean;
  /** Skip the target tooltip and advance to the next step after the lead-in. */
  autoAdvance?: boolean;
  title: string;
  body: string;
}

interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Where the cut-out is, and which selector it was measured from.
 *
 * The selector is carried along because a step change does not re-measure until
 * the page has settled on the new target — until then the cut-out still belongs
 * to the step being left, and anything positioned from it would land in the
 * wrong place.
 */
interface Spotlight {
  selector: string;
  rect: TourRect;
}

interface StepState {
  index: number;
  phase: 'leadIn' | 'target';
}

/**
 * A step opens on its lead-in when it has one that is actually on screen.
 * Reduced-motion shoppers go straight to the target: the sweep is pure motion.
 */
const enterStep = (steps: Step[], index: number): StepState => {
  const selector = steps[index]?.leadInSelector;
  const ready = !!selector && !prefersReducedMotion() && !!document.querySelector(selector);
  return { index, phase: ready ? 'leadIn' : 'target' };
};

interface OnboardingTourProps {
  target: TourTarget;
  shopifyCustomerId?: string;
  /** 'onboarding' for cold-start shoppers: their carousels come from stated picks. */
  personalizationLevel?: string;
  /** Called when the tour finishes or is skipped. */
  onFinish: (completed: boolean) => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({
  target,
  shopifyCustomerId,
  personalizationLevel,
  onFinish,
}) => {
  const { t } = useTranslation();
  const [spotlight, setSpotlight] = useState<Spotlight | null>(null);
  const isScrolling = useRef(false);

  const isCold = personalizationLevel === 'onboarding';

  // Steps: primary spotlight → shelf menu (with scroll preview) → for each
  // other-recipient type: header tooltip, then carousel scroll (auto-advances)
  // → feedback tab. The feedback tab always closes the tour.
  const steps: Step[] = useMemo(() => {
    const recipientSteps: Step[] = target.otherRecipients.flatMap((recipient, idx) => {
      const anchor = `other-recipient-${idx}`;
      return [
        {
          selector: `[data-tour-carousel-header="${anchor}"]`,
          title: t(`tour.recipient.${recipient.type}.title`, {
            defaultValue: t('tour.recipient.gift.title'),
          }),
          body: t(`tour.recipient.${recipient.type}.body`, {
            defaultValue: t('tour.recipient.gift.body'),
            name: recipient.name,
          }),
        },
        {
          selector: `[data-tour-books="${anchor}"]`,
          leadInSelector: `[data-tour-books="${anchor}"]`,
          leadInCaption: t(`tour.recipient.${recipient.type}.leadIn`, {
            defaultValue: t('tour.recipient.gift.leadIn', {
              defaultValue: t('tour.step2.leadIn'),
            }),
            name: recipient.name,
          }),
          autoAdvance: true,
          title: '',
          body: '',
        },
      ];
    });

    return [
      {
        selector: '[data-tour-carousel-header="primary"]',
        title: t('tour.step1.title'),
        body: isCold
          ? t('tour.step1.bodyCold', { name: target.primaryName })
          : t('tour.step1.bodyWarm', { name: target.primaryName }),
      },
      {
        selector: `[${SHELF_MENU_TOUR_ATTR}]`,
        leadInSelector: '[data-tour-books="primary"]',
        leadInCaption: t('tour.step2.leadIn'),
        previewsShelfMenu: true,
        title: t('tour.step2.title'),
        body: t('tour.step2.body'),
      },
      ...recipientSteps,
      {
        selector: `[${FEEDBACK_TAB_TOUR_ATTR}]`,
        title: t('tour.feedback.title'),
        body: t('tour.feedback.body'),
      },
    ];
  }, [t, isCold, target]);

  // Index and phase move as one value, so entering a step can never leave it
  // holding the previous step's phase.
  const [step, setStep] = useState<StepState>(() => enterStep(steps, 0));
  const { index: stepIndex, phase } = step;

  const currentStep = steps[stepIndex];
  const activeSelector =
    phase === 'leadIn' && currentStep.leadInSelector
      ? currentStep.leadInSelector
      : currentStep.selector;

  const measure = useCallback(() => {
    const element = document.querySelector<HTMLElement>(activeSelector);
    if (!element) {
      setSpotlight(null);
      return;
    }
    const box = element.getBoundingClientRect();
    setSpotlight({
      selector: activeSelector,
      rect: {
        top: box.top - SPOTLIGHT_PADDING,
        left: box.left - SPOTLIGHT_PADDING,
        width: box.width + SPOTLIGHT_PADDING * 2,
        height: box.height + SPOTLIGHT_PADDING * 2,
      },
    });
  }, [activeSelector]);

  /** The cut-out has reached the current step: it is safe to position from it. */
  const isOnTarget = spotlight?.selector === activeSelector;

  // The lead-in is choreographed from the moment the spotlight lands on the row,
  // not from the moment the step changes. Started earlier, its first second was
  // spent travelling: the row swept sideways while the page was still scrolling
  // and before the spotlight had reached it.
  useEffect(() => {
    if (step.phase !== 'leadIn' || !isOnTarget) return;
    const timer = setTimeout(() => {
      if (currentStep.autoAdvance && stepIndex < steps.length - 1) {
        setStep(enterStep(steps, stepIndex + 1));
      } else {
        setStep((current) => ({ ...current, phase: 'target' }));
      }
    }, LEAD_IN_MS);
    return () => clearTimeout(timer);
  }, [step, isOnTarget, currentStep, stepIndex, steps]);

  // While the lead-in holds, glide the row sideways and back, so the shopper
  // sees it carries on past the covers on screen. It has to return to the start
  // before the spotlight contracts, or the chip would have scrolled out of view.
  useEffect(() => {
    if (step.phase !== 'leadIn' || !isOnTarget || !currentStep.leadInSelector) return;
    const row = document.querySelector<HTMLElement>(currentStep.leadInSelector);
    if (!row) return;

    const hidden = row.scrollWidth - row.clientWidth;
    if (hidden <= 0) return;
    const reach =
      row.clientWidth * LEAD_IN_GLIDE_FRACTION + coverPitch(row) * LEAD_IN_GLIDE_EXTRA_COVERS;
    const distance = Math.min(hidden, reach);

    let cancelGlide = () => {};
    const out = setTimeout(() => {
      cancelGlide = glideRow(row, distance);
    }, LEAD_IN_GLIDE_OUT_MS);
    const back = setTimeout(() => {
      cancelGlide = glideRow(row, 0);
    }, LEAD_IN_GLIDE_BACK_MS);
    return () => {
      clearTimeout(out);
      clearTimeout(back);
      cancelGlide();
    };
  }, [step, isOnTarget, currentStep.leadInSelector]);

  // Bring the target into view, then hold off measuring until the page is still
  // and has been for a beat. Dimming the screen mid-scroll, with the spotlight
  // chasing the target down the page, is what made the opening feel rushed.
  useEffect(() => {
    const element = document.querySelector<HTMLElement>(activeSelector);
    if (!element) {
      measure();
      return;
    }

    let settle = 0;
    isScrolling.current = true;
    const cancelScroll = scrollToCentre(element, () => {
      settle = window.setTimeout(() => {
        isScrolling.current = false;
        measure();
      }, SETTLE_MS);
    });

    return () => {
      cancelScroll();
      clearTimeout(settle);
      isScrolling.current = false;
    };
  }, [activeSelector, measure]);

  // Keep up with a shopper who scrolls or resizes mid-tour, but stay out of the
  // way of the tour's own scrolling.
  useEffect(() => {
    const track = () => {
      if (!isScrolling.current) measure();
    };
    window.addEventListener('resize', track);
    window.addEventListener('scroll', track, { passive: true });
    return () => {
      window.removeEventListener('resize', track);
      window.removeEventListener('scroll', track);
    };
  }, [measure]);

  // Telling the shopper about the shelf choices while they are hidden behind a
  // chip asks them to take it on faith, so the menu is held open for the step
  // and closed again on the way out. It opens before the step's settle measure,
  // so the spotlight is sized to include it.
  useEffect(() => {
    if (phase !== 'target' || !currentStep.previewsShelfMenu) return;
    const node = document.querySelector(currentStep.selector);
    if (!node) return;

    const setPreview = (open: boolean) =>
      node.dispatchEvent(new CustomEvent(SHELF_MENU_PREVIEW_EVENT, { detail: open }));

    setPreview(true);
    return () => {
      setPreview(false);
    };
  }, [phase, currentStep]);

  const finish = useCallback(
    (completed: boolean) => {
      markTourSeen(shopifyCustomerId);
      onFinish(completed);
    },
    [shopifyCustomerId, onFinish]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [finish]);

  const isLastStep = stepIndex === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      finish(true);
      return;
    }
    setStep(enterStep(steps, stepIndex + 1));
  };

  // A target that never rendered (an empty page, a resized-away element) should
  // not trap the shopper behind a dimmed overlay.
  if (!spotlight) return null;

  const { rect } = spotlight;
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - (rect.top + rect.height);
  const placeBelow = spaceBelow > 190;
  const tooltipTop = placeBelow ? rect.top + rect.height + 12 : Math.max(12, rect.top - 190);
  const tooltipLeft = Math.min(
    Math.max(12, rect.left),
    Math.max(12, window.innerWidth - TOOLTIP_WIDTH - 12)
  );
  // Centred over the lead-in and clear of it, above where there is room.
  const captionTop = rect.top > 80 ? rect.top - 52 : rect.top + rect.height + 16;

  return (
    <div className="fixed inset-0 z-[45]" role="presentation">
      {/* Dimmed page with a cut-out over the target: one element, so there are no
          seams between overlay panels. */}
      <div
        className="pointer-events-none absolute rounded-sm motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-in-out"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 9999px rgba(18, 18, 18, 0.45)',
        }}
      />

      {/* The step tooltip would be pointing at "this menu" while the spotlight is
          still on the whole row, so the lead-in gets a caption of its own. Both
          wait for the cut-out to reach the step: placed against the rect of the
          step being left, they showed up beside the wrong thing and then jumped,
          which read as the same box appearing twice. */}
      {isOnTarget && phase === 'leadIn' && currentStep.leadInCaption ? (
        <p
          className="absolute -translate-x-1/2 border border-indigo-brand-border bg-white px-4 py-2 text-sm font-medium text-indigo-brand-text shadow-lg"
          style={{ top: captionTop, left: rect.left + rect.width / 2 }}
        >
          {currentStep.leadInCaption}
        </p>
      ) : null}

      {phase === 'leadIn' || !isOnTarget ? null : (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="tour-step-title"
          className="absolute w-[300px] border border-indigo-brand-border bg-white p-4 shadow-xl motion-safe:transition-all motion-safe:duration-300"
          style={{ top: tooltipTop, left: tooltipLeft }}
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-indigo-brand-text/50">
            {t('tour.progress', { current: stepIndex + 1, total: steps.length })}
          </p>
          <h2
            id="tour-step-title"
            className="mb-1.5 font-heading text-base font-bold text-indigo-brand-text"
          >
            {currentStep.title}
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-indigo-brand-text/70">
            {currentStep.body}
          </p>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => finish(false)}
              className="text-sm font-medium text-indigo-brand-text/60 underline underline-offset-4 hover:text-indigo-brand-text"
            >
              {t('tour.skip')}
            </button>
            <button
              type="button"
              // biome-ignore lint/a11y/noAutofocus: the tour is an intentional interruption; keyboard users need focus on its primary action
              autoFocus
              onClick={handleNext}
              className="bg-indigo-brand-black px-4 py-2 text-sm font-medium text-white hover:bg-black"
            >
              {isLastStep ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingTour;
